import { useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/api/client";
import { useCreateUser, useMe, useRoles, useUsers } from "@/api/hooks";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Alert, Avatar, PageHeader, Spinner, TextField, formatDate } from "@/components/ui";
import { can } from "@/lib/permissions";

interface Values { full_name: string; email: string; password: string; role: string }

export function UsersPage() {
  const me = useMe();
  const users = useUsers();
  const roles = useRoles();
  const create = useCreateUser();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const { register, handleSubmit, reset, watch } = useForm<Values>({ defaultValues: { role: "sales_rep" } });
  const [dealer, setDealer] = useState<PickerValue | null>(null);
  const isDealer = watch("role") === "dealer";
  const canManage = can(me.data?.permissions ?? [], "users:manage");

  const onSubmit = handleSubmit(async (v) => {
    setErrors({}); setMessage(null);
    try {
      const u = await create.mutateAsync({ ...v, dealer_account_id: isDealer ? dealer?.id ?? null : null });
      reset({ role: v.role, full_name: "", email: "", password: "" });
      setMessage({ tone: "ok", text: `${u.full_name} can now sign in.` });
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors());
      else setMessage({ tone: "danger", text: e instanceof Error ? e.message : "Could not add user" });
    }
  });

  return (
    <>
      <PageHeader title="Users and roles" subtitle="Who can sign in to this workspace and what they can do." />
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card overflow-x-auto lg:col-span-2" aria-label="Users">
          {users.isLoading ? <div className="p-4"><Spinner /></div> : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
                <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Last sign-in</th></tr>
              </thead>
              <tbody>
                {users.data?.map((u) => (
                  <tr key={u.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3"><div className="flex items-center gap-3"><Avatar name={u.full_name} /><div><p className="font-medium">{u.full_name}</p><p className="text-xs text-muted">{u.email}</p></div></div></td>
                    <td className="px-4 py-3">{u.role.name}{u.dealer_name ? <span className="ml-2 text-xs text-muted">({u.dealer_name})</span> : u.role.record_scope === "own" && <span className="ml-2 text-xs text-muted">(own records)</span>}</td>
                    <td className="px-4 py-3 text-muted">{u.last_login_at ? formatDate(u.last_login_at) : "Never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        {canManage && (
          <section className="card p-5" aria-label="Add user">
            <h2 className="mb-4 font-semibold">Add a user</h2>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              {message && <Alert tone={message.tone}>{message.text}</Alert>}
              <TextField label="Full name" {...register("full_name", { required: true })} error={errors.full_name} />
              <TextField label="Email" type="email" {...register("email", { required: true })} error={errors.email} />
              <TextField label="Temporary password" type="password" hint="At least 10 characters" {...register("password", { required: true })} error={errors.password} />
              <div>
                <label htmlFor="role" className="label">Role</label>
                <select id="role" className="input" {...register("role")}>
                  {roles.data?.filter((r) => r.key !== "owner").map((r) => <option key={r.id} value={r.key}>{r.name}</option>)}
                </select>
                {errors.role && <p className="field-error">{errors.role}</p>}
              </div>
              {isDealer && <>
                <RecordPicker label="Dealer account" endpoint="/accounts" params={{ channel: "true" }} value={dealer} onChange={setDealer} error={errors.dealer_account_id} />
                <p className="text-xs text-muted">Dealer users see only the dealer portal: their own leads and orders.</p>
              </>}
              <button type="submit" className="btn-primary w-full" disabled={create.isPending}>Add user</button>
            </form>
          </section>
        )}
      </div>
    </>
  );
}
